import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  CalendarIcon,
  Phone,
  Mail,
  Instagram,
  Check,
  X,
} from "lucide-react";

import { Ornament } from "./Ornament";
import { BRAND, EVENT_TYPES } from "../data/content";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { cn } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  event_type: "",
  message: "",
};

/* Detect where the visitor arrived from — used for the hidden `source` field. */
function detectSource() {
  if (typeof window === "undefined") return "direct";
  try {
    const params = new URLSearchParams(window.location.search);
    const explicit =
      params.get("source") ||
      params.get("utm_source") ||
      params.get("ref");
    if (explicit) {
      const campaign = params.get("utm_campaign");
      const medium = params.get("utm_medium");
      return [explicit, medium, campaign].filter(Boolean).join(" · ");
    }
    if (document.referrer) {
      try {
        const host = new URL(document.referrer).hostname;
        if (host && host !== window.location.hostname) return `referrer:${host}`;
      } catch (_) {
        // ignore malformed referrer
      }
    }
    return `direct:${window.location.pathname}`;
  } catch (_) {
    return "direct";
  }
}

/* ---------------- Success Modal ---------------- */
function SuccessModal({ open, onClose, name }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="success-modal-title"
      data-testid="contact-success-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 animate-[fadeIn_0.4s_ease]"
      onClick={onClose}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes rise { from { opacity: 0; transform: translateY(28px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes tick { 0% { stroke-dashoffset: 60 } 100% { stroke-dashoffset: 0 } }
        @keyframes ring { 0% { opacity: 0; transform: scale(0.6) } 60% { opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
      `}</style>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[var(--lu-black)]/85 backdrop-blur-md" />

      {/* Card */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-[var(--lu-ivory)] border border-[var(--lu-gold)] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.7)] px-8 py-14 sm:px-12 sm:py-16 text-center"
        style={{ animation: "rise 0.6s cubic-bezier(0.16,1,0.3,1) both" }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="contact-success-close"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-[var(--lu-gold)]/50 rounded-full text-[var(--lu-gold-dark)] hover:bg-[var(--lu-gold)] hover:text-[var(--lu-black)] transition-colors"
        >
          <X size={16} />
        </button>

        {/* Deco frame corners */}
        <div className="pointer-events-none absolute inset-3 border border-[var(--lu-gold)]/25" />

        {/* Animated check ring */}
        <div className="relative mx-auto w-24 h-24">
          <span
            className="absolute inset-0 rounded-full border border-[var(--lu-gold)]/50"
            style={{ animation: "ring 0.8s cubic-bezier(0.16,1,0.3,1) 0.1s both" }}
          />
          <span
            className="absolute inset-2 rounded-full border border-[var(--lu-gold)]"
            style={{ animation: "ring 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s both" }}
          />
          <span
            className="absolute inset-4 rounded-full bg-[var(--lu-gold)] flex items-center justify-center"
            style={{ animation: "ring 1s cubic-bezier(0.16,1,0.3,1) 0.35s both" }}
          >
            <Check
              size={30}
              className="text-[var(--lu-black)]"
              strokeWidth={2.4}
            />
          </span>
        </div>

        <p className="mt-8 font-micro text-[10px] tracking-[0.35em] uppercase text-[var(--lu-gold-dark)]">
          Message Received
        </p>
        <h3
          id="success-modal-title"
          className="mt-4 font-serif text-[34px] sm:text-[42px] text-[var(--lu-black)] leading-[1.05]"
        >
          Thank you
          {name ? (
            <>
              ,{" "}
              <span className="italic text-[var(--lu-gold-dark)]">
                {name.trim().split(/\s+/)[0]}
              </span>
            </>
          ) : null}
          .
        </h3>
        <Ornament className="my-6" />
        <p className="text-[15px] leading-[1.85] text-black-70 font-light max-w-sm mx-auto">
          Your inquiry has arrived at our atelier. Stamatina will personally
          reply within{" "}
          <span className="italic text-[var(--lu-gold-dark)]">
            24 hours
          </span>{" "}
          — often much sooner.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="contact-success-dismiss"
            className="btn-gold text-[11px] px-8 py-3"
          >
            Close
          </button>
          <a
            href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
            data-testid="contact-success-call"
            className="btn-gold-outline text-[11px] px-8 py-3"
          >
            Call {BRAND.phone}
          </a>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Contact Section ---------------- */
export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [source, setSource] = useState("");

  useEffect(() => {
    setSource(detectSource());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in Name, Email, Phone and Message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        event_type: form.event_type || null,
        event_date: date ? format(date, "yyyy-MM-dd") : null,
        source: source || null,
      };
      await axios.post(`${API}/contact`, payload);
      setSubmittedName(form.name);
      setSuccessOpen(true);
      setForm(initialForm);
      setDate(null);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <section
      id="contact"
      className="relative bg-[var(--lu-black)] py-24 lg:py-32 grain"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p className="font-micro text-[10px] text-[var(--lu-gold)]">Let's Create</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--lu-ivory)]">Something<span className="italic text-[var(--lu-gold)]">Excellent</span>
          </h2>
          <Ornament className="mt-8" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Info column */}
          <aside className="lg:col-span-4 space-y-8" data-testid="contact-info">
            <div>
              <p className="font-micro text-[10px] text-[var(--lu-gold)]">Studio</p>
              <p className="font-serif text-[24px] text-[var(--lu-ivory)] mt-2">
                Lumière Entertainment
              </p>
              <p className="text-[14px] text-ivory-70 mt-2 max-w-xs">Bespoke productions in Europe, Middle East and Mediterranean.
                By appointment only.</p>
            </div>

            {/* Prominent phone block */}
            <a
              href={`tel:+30${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="contact-phone-hero"
              className="block group border border-[var(--lu-gold)]/60 bg-[var(--lu-onyx)]/40 p-6 hover:border-[var(--lu-gold)] hover:bg-[var(--lu-gold)]/[0.06] transition-all duration-500"
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-full bg-[var(--lu-gold)]/15 border border-[var(--lu-gold)]/60">
                  <Phone size={14} className="text-[var(--lu-gold)]" />
                </span>
                <p className="font-micro text-[10px] tracking-[0.32em] uppercase text-[var(--lu-gold)]">
                  Call the Studio
                </p>
              </div>
              <p
                className="mt-5 font-serif text-[30px] sm:text-[34px] text-[var(--lu-ivory)] group-hover:text-[var(--lu-gold)] transition-colors tracking-wide"
                data-testid="contact-phone-number"
              >
                +30 {BRAND.phone}
              </p>
              <p className="mt-2 text-[12px] text-ivory-60 font-light">
                Mon – Fri · 10:00 – 19:00 (EET)
              </p>
            </a>

            <div className="deco-line max-w-[160px]" />

            <div className="space-y-4">
              <a
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-4 text-[var(--lu-ivory)] hover:text-[var(--lu-gold)] transition-colors"
                data-testid="contact-email-link"
              >
                <span className="w-10 h-10 flex items-center justify-center border border-[var(--lu-gold)]/60 rounded-full">
                  <Mail size={16} className="text-[var(--lu-gold)]" />
                </span>
                <span className="font-serif text-[18px]">{BRAND.email}</span>
              </a>
              <a
                href={`https://instagram.com/${BRAND.instagram}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-4 text-[var(--lu-ivory)] hover:text-[var(--lu-gold)] transition-colors"
                data-testid="contact-instagram"
              >
                <span className="w-10 h-10 flex items-center justify-center border border-[var(--lu-gold)]/60 rounded-full">
                  <Instagram size={16} className="text-[var(--lu-gold)]" />
                </span>
                <span className="font-serif text-[18px]">@{BRAND.instagram}</span>
              </a>
            </div>
          </aside>

          {/* Form column */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-8 border border-[var(--lu-gold)]/40 p-8 lg:p-10 bg-[var(--lu-onyx)]/40"
            data-testid="contact-form"
          >
            {/* Hidden lead-source tracker (URL utm/source/ref → referrer → pathname) */}
            <input
              type="hidden"
              name="source"
              value={source}
              readOnly
              aria-hidden="true"
              tabIndex={-1}
              data-testid="contact-hidden-source"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label className="font-micro text-[10px] text-[var(--lu-gold)]" htmlFor="c-name">Name *</Label>
                <Input
                  id="c-name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                  className="mt-2 bg-transparent border-0 border-b border-[var(--lu-gold)]/50 rounded-none focus-visible:ring-0 focus-visible:border-[var(--lu-gold)] text-[var(--lu-ivory)] text-[16px] placeholder:text-ivory-40 h-11 px-0"
                />
              </div>
              <div>
                <Label className="font-micro text-[10px] text-[var(--lu-gold)]" htmlFor="c-email">
                  Email *
                </Label>
                <Input
                  id="c-email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2 bg-transparent border-0 border-b border-[var(--lu-gold)]/50 rounded-none focus-visible:ring-0 focus-visible:border-[var(--lu-gold)] text-[var(--lu-ivory)] text-[16px] placeholder:text-ivory-40 h-11 px-0"
                />
              </div>

              <div>
                <Label className="font-micro text-[10px] text-[var(--lu-gold)]" htmlFor="c-phone">
                  Phone Number *
                </Label>
                <Input
                  id="c-phone"
                  type="tel"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+30 694 000 0000"
                  className="mt-2 bg-transparent border-0 border-b border-[var(--lu-gold)]/50 rounded-none focus-visible:ring-0 focus-visible:border-[var(--lu-gold)] text-[var(--lu-ivory)] text-[16px] placeholder:text-ivory-40 h-11 px-0"
                />
              </div>

              <div>
                <Label className="font-micro text-[10px] text-[var(--lu-gold)]">Event Type</Label>
                <Select
                  value={form.event_type}
                  onValueChange={(v) => update("event_type", v)}
                >
                  <SelectTrigger
                    data-testid="contact-select-event-type"
                    className="mt-2 bg-transparent border-0 border-b border-[var(--lu-gold)]/50 rounded-none focus:ring-0 text-[var(--lu-ivory)] text-[16px] h-11 px-0 data-[placeholder]:text-ivory-40"
                  >
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--lu-black)] border-[var(--lu-gold)]/50 text-[var(--lu-ivory)]">
                    {EVENT_TYPES.map((t) => (
                      <SelectItem
                        key={t}
                        value={t}
                        data-testid={`event-type-option-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        className="focus:bg-[var(--lu-gold)] focus:text-[var(--lu-black)]"
                      >
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label className="font-micro text-[10px] text-[var(--lu-gold)]">Event Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="contact-calendar-trigger"
                      className={cn(
                        "mt-2 w-full h-11 flex items-center justify-between border-0 border-b border-[var(--lu-gold)]/50 bg-transparent text-[16px] text-left focus:outline-none focus:border-[var(--lu-gold)] px-0",
                        !date && "text-ivory-40",
                        date && "text-[var(--lu-ivory)]"
                      )}
                    >
                      {date ? format(date, "PPP") : "Select a date"}
                      <CalendarIcon size={16} className="text-[var(--lu-gold)]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-auto p-0 bg-[var(--lu-black)] border-[var(--lu-gold)]/50 text-[var(--lu-ivory)]"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      initialFocus
                      data-testid="contact-calendar-date"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="mt-6">
              <Label className="font-micro text-[10px] text-[var(--lu-gold)]" htmlFor="c-message">Message *</Label>
              <Textarea
                id="c-message"
                data-testid="contact-textarea-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Number of guests, space, style, dreams..."
                rows={5}
                className="mt-2 bg-transparent border border-[var(--lu-gold)]/40 rounded-none focus-visible:ring-0 focus-visible:border-[var(--lu-gold)] text-[var(--lu-ivory)] placeholder:text-ivory-40 resize-none"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="text-[12px] text-ivory-50 max-w-sm">We respond personally within 24 hours. Your information remains confidential.</p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="btn-gold disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </div>
          </form>
        </div>
      </div>

      <SuccessModal
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        name={submittedName}
      />
    </section>
  );
}
